import React, { FC, useEffect } from 'react'
import { Alert, Form, Input, Select } from 'antd'
import { FormInstance } from 'antd/es/form/hooks/useForm'

import '../index.less'
import { HubEnum, hubsTitles, PRACTITIONER_FHC_URL_IDENTIFIER, PRACTITIONER_HUB_ID_IDENTIFIER } from '../../../../constants'
import { useCreateOrUpdatePractitionerMutation, useGetPractitionerQuery } from '../../../../core/api/practitionerApi'
import { skipToken } from '@reduxjs/toolkit/query'
import { HealthcareParty } from '@icure/cardinal-sdk'
import { SpinLoader } from '../../../common/SpinLoader'
import { getDecryptedPropertyStub } from '../../../../helpers/property'
import { createQualityTag, extractQualityFromPractitioner, filterOutQualityCodes, getAvailableQualities, qualityToCode, QualityType } from '../../../../helpers/qualityTags'

interface HubsConnectionProps {
  practitionerId?: string
  form: FormInstance
  t: (key: string, ctxt?: { [key: string]: string }) => string
  onClose: () => void
}

export const HubsConnection: FC<HubsConnectionProps> = ({ practitionerId, form, t, onClose }) => {
  // Download the user's private key from the Cardinal SDK and trigger a file download
  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(practitionerId ?? skipToken, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const [updatePractitioner, { isSuccess: isPractitionerUpdatedSuccessfully, isLoading: isPractitionerUpdatingLoading }] = useCreateOrUpdatePractitionerMutation()
  useEffect(() => {
    if (isPractitionerUpdatedSuccessfully) {
      onClose()
    }
  }, [isPractitionerUpdatedSuccessfully])

  const handleSubmit = (values: { ssin: string; hubId: string; fhcUrl: string; quality: QualityType }) => {
    const { ssin, hubId, fhcUrl, quality } = values

    const hubProperty = getDecryptedPropertyStub(PRACTITIONER_HUB_ID_IDENTIFIER, hubId)
    const fhcProperty = getDecryptedPropertyStub(PRACTITIONER_FHC_URL_IDENTIFIER, fhcUrl)

    updatePractitioner(
      new HealthcareParty({
        ...practitioner,
        ssin,
        type: qualityToCode(quality),
        codes: [...filterOutQualityCodes(practitioner?.codes), createQualityTag(quality)],
        properties: [
          ...(practitioner?.properties?.filter((p) => p?.type?.identifier !== PRACTITIONER_HUB_ID_IDENTIFIER && p?.type?.identifier !== PRACTITIONER_FHC_URL_IDENTIFIER) ?? []),
          hubProperty,
          fhcProperty,
        ],
      }),
    )
  }

  const initialValues = {
    ssin: practitioner?.ssin,
    hubId: practitioner?.properties?.find((p) => p?.type?.identifier === PRACTITIONER_HUB_ID_IDENTIFIER)?.typedValue?.stringValue,
    fhcUrl: practitioner?.properties?.find((p) => p?.type?.identifier === PRACTITIONER_FHC_URL_IDENTIFIER)?.typedValue?.stringValue,
    quality: extractQualityFromPractitioner(practitioner?.type, practitioner?.codes),
  }

  const availableHubs = [
    { value: HubEnum.RSW, label: hubsTitles.rsw },
    { value: HubEnum.RSB, label: hubsTitles.rsb },
    { value: HubEnum.COZO, label: hubsTitles.cozo },
    { value: HubEnum.VITALINK, label: hubsTitles.vitalink },
  ]
  const availableFhcUrls = [
    { label: 'Production', value: 'https://fhcprd.icure.cloud' },
    { label: 'Acceptance', value: 'https://fhcacc.icure.cloud' },
  ]
  return (
    <>
      {(isPractitionerUpdatingLoading || isPractitionerFetching) && <SpinLoader />}
      <Form
        className="modalManageAccountForm__form"
        layout="vertical"
        onFinish={(values) => {
          handleSubmit(values)
        }}
        colon={false}
        form={form}
        initialValues={initialValues}
      >
        <Alert message={t('modalManageAccountForm.hubs.alert')} type="info" showIcon={true} style={{ width: '100%' }} />
        <div className="modalManageAccountForm__form__inputs">
          <Form.Item name="ssin" label={t('modalManageAccountForm.hubs.ssin')} rules={[{ required: true, message: t('modalManageAccountForm.hubs.ssinIsRequired') }]}>
            <Input placeholder={t('modalManageAccountForm.hubs.ssin')} size="large" />
          </Form.Item>
          <Form.Item name="quality" label={t('modalManageAccountForm.hubs.quality')} rules={[{ required: true, message: t('modalManageAccountForm.hubs.qualityIsRequired') }]}>
            <Select size="large" options={getAvailableQualities(t)} placeholder={t('modalManageAccountForm.hubs.quality')} />
          </Form.Item>
          <Form.Item
            name="hubId"
            label={t('modalManageAccountForm.hubs.regionalHubs')}
            rules={[{ required: true, message: t('modalManageAccountForm.hubs.regionalHubsIsRequired') }]}
          >
            <Select size="large" options={availableHubs} placeholder={t('modalManageAccountForm.hubs.regionalHubs')} />
          </Form.Item>
          <Form.Item name="fhcUrl" label={t('modalManageAccountForm.hubs.fhcUrl')} rules={[{ required: true, message: t('modalManageAccountForm.hubs.fhcUrlIsRequired') }]}>
            <Select size="large" options={availableFhcUrls} placeholder={t('modalManageAccountForm.hubs.fhcUrl')} />
          </Form.Item>
        </div>
      </Form>
    </>
  )
}
